import type { ReactNode } from "react";
import {
  CallOutcome,
  CommitmentClosedReason,
  CommitmentState,
  DonorTimelineKind,
  FollowUpChannel,
  FollowUpRegister,
  FollowUpResult,
  assertNever,
  type DonorTimelineItemDto,
  type TimelineCommitmentDto,
} from "@signalvote/shared";
import { BookOpen, HandCoins, Mail, MessageSquare, NotebookPen, PhoneCall } from "lucide-react";
import {
  formatCampaignClockTime,
  getCampaignTodayString,
} from "@/lib/calendarUtils";
import { daysAgo, shortDay } from "./activityDay";

const OUTCOME: Record<CallOutcome, string> = {
  [CallOutcome.NoAnswer]: "No answer",
  [CallOutcome.Voicemail]: "Left a voicemail",
  [CallOutcome.CallBackRequested]: "Asked for a call back",
  [CallOutcome.Committed]: "Committed",
  [CallOutcome.Declined]: "Declined",
  [CallOutcome.WrongNumber]: "Wrong number",
  [CallOutcome.RemovalRequested]: "Asked not to be called",
};

const REGISTER: Record<FollowUpRegister, string> = {
  [FollowUpRegister.ThankYou]: "Thank-you",
  [FollowUpRegister.MissedYou]: "Missed-you",
  [FollowUpRegister.ThanksForYourTime]: "Thanks-for-your-time",
  [FollowUpRegister.DonationLink]: "Donation-link",
};

// Only what the phone reported: iOS says sent or cancelled; everything else only knows it was opened.
const REPORTED: Record<FollowUpResult, string> = {
  [FollowUpResult.Sent]: "sent",
  [FollowUpResult.Cancelled]: "opened, then cancelled",
  [FollowUpResult.Unknown]: "opened on the phone",
};

const CLOSED: Record<CommitmentClosedReason, string> = {
  [CommitmentClosedReason.ReceivedElsewhere]: "received elsewhere",
  [CommitmentClosedReason.Withdrawn]: "withdrawn",
  [CommitmentClosedReason.Lapsed]: "lapsed",
  [CommitmentClosedReason.Superseded]: "replaced by a later commitment",
  [CommitmentClosedReason.LoggedInError]: "logged in error",
};

// A commitment keeps its cents; "$500", but "$250.50".
const wholeDollars = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const cents = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});
const amount = (n: number) =>
  (Number.isInteger(n) ? wholeDollars : cents).format(n);

/** "Committed $500 · expected by Oct 5 · not in yet" — one line, on the call that produced it. */
function commitmentLine(commitment: TimelineCommitmentDto) {
  const state =
    commitment.state === CommitmentState.Open
      ? [
          commitment.expectBy
            ? `expected by ${shortDay(commitment.expectBy)}`
            : "no date given",
          "not in yet",
        ]
      : [
          `closed, ${commitment.closedReason ? CLOSED[commitment.closedReason] : "no reason given"}`,
        ];
  return [`Committed ${amount(commitment.amount)}`, ...state].join(" · ");
}

/**
 * A person's activity: the same timeline the Caller sees on the phone's donor profile, built by the one
 * server timeline (`DonorTimeline`). Newest first, each entry on its campaign-local day and clock.
 */
export function ProspectTimeline({ items }: { items: DonorTimelineItemDto[] }) {
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nobody has called them yet. Calls, texts and commitments will show here.
      </p>
    );
  }

  const today = getCampaignTodayString();
  return (
    <ol aria-label="Activity">
      {items.map((item, index) => (
        <Entry
          key={`${item.kind}-${item.at}-${index}`}
          item={item}
          today={today}
          last={index === items.length - 1}
        />
      ))}
    </ol>
  );
}

function Entry({
  item,
  today,
  last,
}: {
  item: DonorTimelineItemDto;
  today: string;
  last: boolean;
}) {
  const { icon, title, details } = describe(item);
  return (
    <li className="grid grid-cols-[28px_1fr] gap-3">
      <div className="flex flex-col items-center">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-muted text-muted-foreground">
          {icon}
        </span>
        {!last && <span className="my-1 w-px flex-1 bg-border" />}
      </div>
      <div className="pb-4">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">
          {daysAgo(item.on, today)} · {formatCampaignClockTime(item.at)}
        </p>
        {details.length > 0 && (
          <p className="mt-0.5 text-sm text-muted-foreground">
            {details.join(" · ")}
          </p>
        )}
      </div>
    </li>
  );
}

function describe(item: DonorTimelineItemDto): {
  icon: ReactNode;
  title: string;
  details: string[];
} {
  switch (item.kind) {
    case DonorTimelineKind.Call: {
      const call = item.call!;
      return {
        icon: call.commitment ? (
          <HandCoins className="h-3.5 w-3.5" />
        ) : (
          <PhoneCall className="h-3.5 w-3.5" />
        ),
        // A commitment is part of its call, never an entry beside it.
        title: call.commitment
          ? commitmentLine(call.commitment)
          : call.outcome === null
            ? "Called — outcome not logged"
            : OUTCOME[call.outcome],
        details: [
          ...(call.note ? [`“${call.note}”`] : []),
          ...(call.callBackOn
            ? [`Call back ${shortDay(call.callBackOn)}`]
            : []),
          ...(call.callerName ? [`Called by ${call.callerName}`] : []),
        ],
      };
    }
    case DonorTimelineKind.FollowUp: {
      const { channel, register, result } = item.followUp!;
      const isText = channel === FollowUpChannel.Text;
      return {
        icon: isText ? (
          <MessageSquare className="h-3.5 w-3.5" />
        ) : (
          <Mail className="h-3.5 w-3.5" />
        ),
        title: `${REGISTER[register]} ${isText ? "text" : "email"} ${REPORTED[result]}`,
        details: [],
      };
    }
    case DonorTimelineKind.CheckIn: {
      // A Fundraiser's note on an open commitment — an annotation, never a change to it.
      const { amount: committed, note, byName } = item.checkIn!;
      return {
        icon: <NotebookPen className="h-3.5 w-3.5" />,
        title: `Checked in on ${amount(committed)} committed`,
        details: [`“${note}”`, ...(byName ? [byName] : [])],
      };
    }
    case DonorTimelineKind.AddedToCallBook: {
      const { name, ask } = item.addedToCallBook!;
      return {
        icon: <BookOpen className="h-3.5 w-3.5" />,
        title: `Added to ${name}`,
        details: ask === null ? [] : [`Ask when added ${wholeDollars.format(ask)}`],
      };
    }
    default:
      return assertNever(item.kind);
  }
}
